<template>
  <div class="family-card" :layout="layoutRef" :selected="selectedRef" :class="{ dimmed: props.dimmed }">
    <div class="father">
      <ParentConnector v-if="!props.noConnector" :layout="layoutRef" :dimmed="props.dimmed"/>
      <div v-else></div>
      <PersonCard
        :personId="fatherIdRef"
        :layout="layoutRef"
        :show-profile-type="props.showProfileType"
        show-profile-count="multiple"
        :select-mode="selectMode"
        :static="props.static"
        :no-hover="noHover"
        v-model:selectedId="selectedFatherIdRef"
        :disabled="disabled"
        @click="onSelected(FamilyRelationshipRole.Father)"/>
    </div>
    <div class="mother">
      <ParentConnector v-if="!props.noConnector" :layout="layoutRef" :dimmed="props.dimmed" mother/>
      <div v-else></div>
      <PersonCard
        :personId="motherIdRef"
        :layout="layoutRef"
        :show-profile-type="showProfileType"
        show-profile-count="multiple"
        :select-mode="selectMode"
        :static="props.static"
        :no-hover="noHover"
        v-model:selectedId="selectedMotherIdRef"
        :disabled="disabled"
        @click="onSelected(FamilyRelationshipRole.Mother)"/>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.family-card {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &[layout="dot"] {
    gap: 0;
  }

  & > .father, & > .mother { // prevent styles from leaking into ParentConnector
    display: flex;
    max-width: 500px;

    .person-card {
      min-width: 0;
    }
  }
}

</style>

<script setup lang="ts">
import { computed, PropType } from 'vue'
import { useViewFamilyStore } from '@/rd/ViewFamilyStore'
import { FamilyRelationshipRole } from '@/rd/ResearchDataModel'
import ParentConnector from './ParentConnector.vue'
import PersonCard from './PersonCard.vue'

const props = defineProps({
  familyId: String,
  layout: String as PropType<"small" | "tiny" | "dot">,
  noConnector: Boolean,
  showProfileType: Boolean,
  dimmed: Boolean,
  disabled: Boolean,
  selectMode: Boolean,
  static: Boolean,
  noHover: Boolean,
  selectedId: String,
})

const emit = defineEmits([
  'click',
  'update:selectedId',
])

const viewFamilyStore = useViewFamilyStore()

const layoutRef = computed(() => props.layout ?? 'small')

const viewFamilyRef = computed(() => viewFamilyStore.getAsyncFamily(props.familyId)?.data)
// NOTE: if we have a valid family id but can't see the family, use fake father and mother ids, so they will appear
// as valid but not visible, instead of empty
const fatherIdRef = computed(() => props.familyId ? (viewFamilyRef.value ? viewFamilyRef.value.fatherId : '000-000') : undefined)
const motherIdRef = computed(() => props.familyId ? (viewFamilyRef.value ? viewFamilyRef.value.motherId : '000-000') : undefined)

// person-card for each spouse must be bound to a selected person id, but family card selection represents 
// the whole family, so provide a property the person-card can bind to that is equal to the spouse id if the 
// family is selected

const selectedRef = computed({
  get() { return props.selectMode && props.selectedId == props.familyId },
  set(newValue) { emit('update:selectedId', newValue ? props.familyId : undefined) }
})

const selectedFatherIdRef = computed({
  get() { return selectedRef.value ? viewFamilyRef.value?.fatherId : undefined },
  set(newValue) { selectedRef.value = !!newValue }
})

const selectedMotherIdRef = computed({
  get() { return selectedRef.value ? viewFamilyRef.value?.motherId : undefined },
  set(newValue) { selectedRef.value = !!newValue }
})

function onSelected(spouseRole: FamilyRelationshipRole) {
  emit('click', spouseRole)
}
</script>
